import {
  Circle,
  Droplet,
  Flame,
  Heart,
  Lock,
  Moon,
  Sparkles,
  Star,
  Sun,
  Users,
  Wind,
  Zap,
} from "lucide-react";
import {
  IconBulb,
  IconClock,
  IconCoinOff,
  IconEyeOff,
  IconMoodSad,
  IconUserHeart,
} from "@tabler/icons-react";
import type { LandingContent } from "./content";
import { getLandingContent } from "./content";

type UnknownRecord = Record<string, unknown>;

const trilhaIcons = {
  Circle,
  Droplet,
  Flame,
  Heart,
  Lock,
  Moon,
  Sparkles,
  Star,
  Sun,
  Users,
  Wind,
  Zap,
} as const;

const objecaoIcons = {
  IconBulb,
  IconClock,
  IconCoinOff,
  IconEyeOff,
  IconMoodSad,
  IconUserHeart,
} as const;

export interface LandingPageHeroHighlight {
  num: string;
  label: string;
}

export interface LandingPageHeroSlide {
  url: string;
  alt: string;
  overlay: string;
}

export interface LandingPageHero {
  cta_primary: string;
  cta_secondary: string;
  badge?: string;
  title?: string;
  title_part1?: string;
  title_part2?: string;
  subtitle?: string;
  bottom_label?: string;
  bg_mobile?: string;
  slides?: Array<Partial<LandingPageHeroSlide>>;
}

export interface LandingPageTrilha {
  id: string;
  num: string;
  nome: string;
  icon: typeof Sparkles;
  cor: string;
  gradiente: string;
  descricao: string;
  imagem: string;
  imagemAlt: string;
  quizResultText?: string;
}

export interface LandingPagePlano {
  nome: string;
  tagline: string;
  preco: string;
  centavos: string;
  periodo: string;
  popular: boolean;
  recursos: string[];
  cta: string;
  link: string;
}

export interface LandingPageInstrutorAula {
  titulo: string;
  duracao: string;
}

export interface LandingPageInstrutor {
  nome: string;
  especialidade: string;
  foto: string;
  bio: string;
  badge: string;
  trilhas: string[];
  aulas: LandingPageInstrutorAula[];
  citacao: string;
  gradiente: string;
}

export interface LandingPageBlogPreviewPost {
  id: number;
  slug: string;
  url: string;
  titulo: string;
  categorias: string[];
  imagem: string;
  imagemAlt: string;
  excerpt: string;
}

export interface LandingPageBlogPreview {
  title: string;
  subtitle: string;
  count: number;
  posts: Array<Partial<LandingPageBlogPreviewPost>>;
}

export interface LandingPageContrasteRow {
  tema: string;
  problema: string;
  solucao: string;
}

export interface LandingPageObjecao {
  Icon: typeof IconCoinOff;
  problema: string;
  solucao: string;
}

export interface LandingPageStatsBar {
  label: string;
  pct: number;
  dim?: boolean;
}

export interface LandingPageStats {
  title: string;
  subtitle: string;
  bigPct: number;
  bigText: string;
  secondaryPct: number;
  secondaryText: string;
  donutF: number;
  donutM: number;
  bars1: Array<Partial<LandingPageStatsBar>>;
  bars2: Array<Partial<LandingPageStatsBar>>;
  source: string;
  femaleGrowth: string;
}

export interface LandingPageDiferencialCard {
  icon: typeof Sparkles;
  titulo: string;
  texto: string;
}

export interface LandingPageDiferenciais {
  title: string;
  subtitle: string;
  cards: Array<Partial<LandingPageDiferencialCard>>;
}

export interface LandingPageCTA {
  title: string;
  subtitle: string;
  button: string;
  link: string;
  disclaimer: string;
}

export interface LandingPageQuizOption {
  label: string;
  scores: Record<string, number>;
}

export interface LandingPageQuizQuestion {
  id: string;
  texto: string;
  subtexto: string | null;
  opcoes: LandingPageQuizOption[];
}

export interface LandingPageQuiz {
  title: string;
  subtitle: string;
  ctaTrilhaLink: string;
  questions: LandingPageQuizQuestion[];
}

export interface LandingPageFooterFeaturedPost {
  title: string;
  url: string;
}

export interface LandingPageSEO {
  title?: string;
  description?: string;
  canonical?: string;
  og_title?: string;
  og_desc?: string;
  og_image?: string;
  og_site?: string;
  og_locale?: string;
  seo_fallback_image?: string;
  tw_card?: "summary" | "summary_large_image";
  tw_title?: string;
  tw_desc?: string;
  tw_image?: string;
  ga4_id?: string;
}

export interface LandingPageData {
  hero: LandingPageHero;
  heroHighlights: Array<Partial<LandingPageHeroHighlight>>;
  trilhas: Array<Partial<LandingPageTrilha>>;
  assinaturaPlanos: Array<Partial<LandingPagePlano>>;
  instrutores: Array<Partial<LandingPageInstrutor>>;
  blogPreview: Partial<LandingPageBlogPreview>;
  footerFeaturedPosts: Array<Partial<LandingPageFooterFeaturedPost>>;
  contrasteRows: Array<Partial<LandingPageContrasteRow>>;
  objecoes: Array<Partial<LandingPageObjecao>>;
  stats: Partial<LandingPageStats>;
  diferenciais: Partial<LandingPageDiferenciais>;
  cta: Partial<LandingPageCTA>;
  quiz: Partial<LandingPageQuiz>;
  seo?: Partial<LandingPageSEO>;
}

const FALLBACK_HERO: LandingPageHero = {
  cta_primary: "Comecar agora",
  cta_secondary: "Ver as trilhas",
};

function asRecord(value: unknown): UnknownRecord | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  return value as UnknownRecord;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function pickText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function pickBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function pickNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function pickStringArray(value: unknown): string[] | undefined {
  const items = asArray(value)
    .map((item) => pickText(item))
    .filter((item): item is string => Boolean(item));

  return items.length > 0 ? items : undefined;
}

function withDefinedValues<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined),
  ) as Partial<T>;
}

function mapCollection<T extends object>(
  value: unknown,
  mapper: (item: unknown) => Partial<T> | null,
): Array<Partial<T>> {
  return asArray(value)
    .map((item) => mapper(item))
    .filter((item): item is Partial<T> => Boolean(item));
}

function pickTrilhaIcon(value: unknown): LandingPageTrilha["icon"] | undefined {
  const iconName = pickText(value);

  if (!iconName || !(iconName in trilhaIcons)) {
    return undefined;
  }

  return trilhaIcons[iconName as keyof typeof trilhaIcons];
}

function pickObjecaoIcon(value: unknown): LandingPageObjecao["Icon"] | undefined {
  const iconName = pickText(value);

  if (!iconName || !(iconName in objecaoIcons)) {
    return undefined;
  }

  return objecaoIcons[iconName as keyof typeof objecaoIcons];
}

function mapHeroHighlight(item: unknown): Partial<LandingPageHeroHighlight> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    num: pickText(record.num),
    label: pickText(record.label),
  }) as Partial<LandingPageHeroHighlight>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapHeroSlide(item: unknown): Partial<LandingPageHeroSlide> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const url =
    pickText(record.url) ??
    pickText(record.image) ??
    pickText(record.imagem) ??
    pickText(record.img) ??
    pickText(record.bg_img) ??
    pickText(record.bgImg);

  if (!url) {
    return null;
  }

  return withDefinedValues({
    url,
    alt:
      pickText(record.alt) ??
      pickText(record.imagem_alt) ??
      pickText(record.imagemAlt) ??
      pickText(record.image_alt) ??
      pickText(record.imageAlt),
    overlay:
      pickText(record.overlay) ??
      pickText(record.gradiente) ??
      pickText(record.gradient),
  }) as Partial<LandingPageHeroSlide>;
}

function mapTrilha(item: unknown): Partial<LandingPageTrilha> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    id: pickText(record.id),
    num: pickText(record.num),
    nome: pickText(record.nome),
    icon: pickTrilhaIcon(record.icon),
    cor: pickText(record.cor),
    gradiente: pickText(record.gradiente),
    descricao: pickText(record.descricao),
    quizResultText:
      pickText(record.quiz_result_text) ??
      pickText(record.quizResultText) ??
      pickText(record.quiz_result) ??
      pickText(record.quizResult),
    imagem: pickText(record.imagem),
    imagemAlt: pickText(record.imagem_alt) ?? pickText(record.imagemAlt),
  }) as Partial<LandingPageTrilha>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapPlano(item: unknown): Partial<LandingPagePlano> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    nome: pickText(record.nome),
    tagline: pickText(record.tagline),
    preco: pickText(record.preco),
    centavos: pickText(record.centavos),
    periodo: pickText(record.periodo),
    popular: pickBoolean(record.popular),
    recursos: pickStringArray(record.recursos),
    cta: pickText(record.cta),
    link: pickText(record.link),
  }) as Partial<LandingPagePlano>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapInstrutorAula(item: unknown): Partial<LandingPageInstrutorAula> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    titulo: pickText(record.titulo),
    duracao: pickText(record.duracao),
  }) as Partial<LandingPageInstrutorAula>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapInstrutor(item: unknown): Partial<LandingPageInstrutor> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const aulas = mapCollection<LandingPageInstrutorAula>(record.aulas, mapInstrutorAula);

  const mapped = withDefinedValues({
    nome: pickText(record.nome),
    especialidade: pickText(record.especialidade),
    foto: pickText(record.foto),
    bio: pickText(record.bio),
    badge: pickText(record.badge),
    trilhas: pickStringArray(record.trilhas),
    aulas: aulas.length > 0 ? (aulas as LandingPageInstrutorAula[]) : undefined,
    citacao: pickText(record.citacao),
    gradiente: pickText(record.gradiente),
  }) as Partial<LandingPageInstrutor>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapBlogPreviewPost(item: unknown): Partial<LandingPageBlogPreviewPost> | null {
  const record = asRecord(item);
  if (!record) return null;

  const rawUrl = pickText(record.url);
  const slug = pickText(record.slug);
  let url = rawUrl;
  if (url) {
    url = url.replace(/^\/blog\/([^/]+)\/?$/, '/$1/');
  } else if (slug) {
    url = `/${slug.replace(/^\/+|\/+$/g, '')}/`;
  }

  const mapped = withDefinedValues({
    id: pickNumber(record.id),
    slug,
    url,
    titulo: pickText(record.titulo) ?? pickText(record.title),
    categorias: pickStringArray(record.categorias),
    imagem: pickText(record.imagem),
    imagemAlt: pickText(record.imagem_alt) ?? pickText(record.imagemAlt),
    excerpt: pickText(record.excerpt),
  }) as Partial<LandingPageBlogPreviewPost>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}


function mapBlogPreview(value: unknown): Partial<LandingPageBlogPreview> {
  const record = asRecord(value);
  if (!record) return {};

  const posts = mapCollection<LandingPageBlogPreviewPost>(record.posts, mapBlogPreviewPost);

  return withDefinedValues({
    title: pickText(record.title),
    subtitle: pickText(record.subtitle),
    count: pickNumber(record.count),
    posts: posts.length > 0 ? posts : undefined,
  }) as Partial<LandingPageBlogPreview>;
}

function mapContrasteRow(item: unknown): Partial<LandingPageContrasteRow> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    tema: pickText(record.tema),
    problema: pickText(record.problema),
    solucao: pickText(record.solucao),
  }) as Partial<LandingPageContrasteRow>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapObjecao(item: unknown): Partial<LandingPageObjecao> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    Icon: pickObjecaoIcon(record.icon) ?? pickObjecaoIcon(record.Icon),
    problema: pickText(record.problema),
    solucao: pickText(record.solucao),
  }) as Partial<LandingPageObjecao>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapStatsBar(item: unknown): Partial<LandingPageStatsBar> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    label: pickText(record.label),
    pct: pickNumber(record.pct),
    dim: pickBoolean(record.dim),
  }) as Partial<LandingPageStatsBar>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapStats(value: unknown): Partial<LandingPageStats> {
  const record = asRecord(value);

  if (!record) {
    return {};
  }

  const bars1 = mapCollection<LandingPageStatsBar>(record.bars1, mapStatsBar);
  const bars2 = mapCollection<LandingPageStatsBar>(record.bars2, mapStatsBar);

  return withDefinedValues({
    title: pickText(record.title),
    subtitle: pickText(record.subtitle),
    bigPct: pickNumber(record.big_pct) ?? pickNumber(record.bigPct),
    bigText: pickText(record.big_text) ?? pickText(record.bigText),
    secondaryPct:
      pickNumber(record.secondary_pct) ?? pickNumber(record.secondaryPct),
    secondaryText:
      pickText(record.secondary_text) ?? pickText(record.secondaryText),
    donutF: pickNumber(record.donut_f) ?? pickNumber(record.donutF),
    donutM: pickNumber(record.donut_m) ?? pickNumber(record.donutM),
    bars1: bars1.length > 0 ? bars1 : undefined,
    bars2: bars2.length > 0 ? bars2 : undefined,
    source: pickText(record.source),
    femaleGrowth:
      pickText(record.female_growth) ?? pickText(record.femaleGrowth),
  }) as Partial<LandingPageStats>;
}

function mapDiferencialCard(
  item: unknown,
): Partial<LandingPageDiferencialCard> | null {
  const record = asRecord(item);

  if (!record) {
    return null;
  }

  const mapped = withDefinedValues({
    icon: pickTrilhaIcon(record.icon),
    titulo: pickText(record.titulo) ?? pickText(record.title),
    texto: pickText(record.texto) ?? pickText(record.text),
  }) as Partial<LandingPageDiferencialCard>;

  return Object.keys(mapped).length > 0 ? mapped : null;
}

function mapDiferenciais(value: unknown): Partial<LandingPageDiferenciais> {
  const record = asRecord(value);

  if (!record) {
    return {};
  }

  const cards = mapCollection<LandingPageDiferencialCard>(
    record.cards ?? record.items,
    mapDiferencialCard,
  );

  return withDefinedValues({
    title: pickText(record.title),
    subtitle: pickText(record.subtitle),
    cards: cards.length > 0 ? cards : undefined,
  }) as Partial<LandingPageDiferenciais>;
}

function mapCTA(value: unknown): Partial<LandingPageCTA> {
  const record = asRecord(value);

  if (!record) {
    return {};
  }

  return withDefinedValues({
    title: pickText(record.title),
    subtitle: pickText(record.subtitle),
    button:
      pickText(record.button) ??
      pickText(record.cta) ??
      pickText(record.label),
    link: pickText(record.link),
    disclaimer: pickText(record.disclaimer),
  }) as Partial<LandingPageCTA>;
}

function mapQuizOption(item: unknown): Partial<LandingPageQuizOption> | null {
  const record = asRecord(item);
  if (!record) return null;

  const label = pickText(record.label);
  const scoresRecord = asRecord(record.scores);
  const scores = scoresRecord
    ? Object.fromEntries(
        Object.entries(scoresRecord)
          .map(([key, value]) => [key, pickNumber(value)])
          .filter(([, value]) => value !== undefined),
      )
    : {};

  if (!label || Object.keys(scores).length === 0) return null;
  return { label, scores: scores as Record<string, number> };
}

function mapQuizQuestion(item: unknown): Partial<LandingPageQuizQuestion> | null {
  const record = asRecord(item);
  if (!record) return null;

  const id = pickText(record.id);
  const texto = pickText(record.texto);
  const opcoes = mapCollection<LandingPageQuizOption>(record.opcoes, mapQuizOption);

  if (!id || !texto || opcoes.length === 0) return null;

  return withDefinedValues({
    id,
    texto,
    subtexto: pickText(record.subtexto) ?? null,
    opcoes,
  }) as Partial<LandingPageQuizQuestion>;
}

function mapQuiz(value: unknown): Partial<LandingPageQuiz> {
  const record = asRecord(value);
  if (!record) return {};

  const questions = mapCollection<LandingPageQuizQuestion>(record.questions, mapQuizQuestion);

  return withDefinedValues({
    title: pickText(record.title),
    subtitle: pickText(record.subtitle),
    ctaTrilhaLink:
      pickText(record.cta_trilha) ??
      pickText(record.ctaTrilha) ??
      pickText(record.cta_trilha_link) ??
      pickText(record.ctaTrilhaLink),
    questions: questions.length > 0 ? questions : undefined,
  }) as Partial<LandingPageQuiz>;
}

function mapFooterFeaturedPost(value: unknown): Partial<LandingPageFooterFeaturedPost> | null {
  const record = asRecord(value);
  if (!record) return null;

  const mapped = withDefinedValues({
    title: pickText(record.titulo) ?? pickText(record.title),
    url: pickText(record.url) ?? pickText(record.href),
  }) as Partial<LandingPageFooterFeaturedPost>;

  if (!mapped.title || !mapped.url) {
    return null;
  }

  return mapped;
}

function pickTwCard(value: unknown): LandingPageSEO["tw_card"] | undefined {
  const s = pickText(value);
  if (s === "summary" || s === "summary_large_image") {
    return s;
  }
  return undefined;
}

function mapSEO(value: unknown): Partial<LandingPageSEO> {
  const record = asRecord(value);
  if (!record) return {};

  return withDefinedValues({
    title: pickText(record.title),
    description: pickText(record.description),
    canonical: pickText(record.canonical),
    og_title: pickText(record.og_title) ?? pickText(record.ogTitle),
    og_desc: pickText(record.og_desc) ?? pickText(record.ogDesc),
    og_image: pickText(record.og_image) ?? pickText(record.ogImage),
    og_site: pickText(record.og_site) ?? pickText(record.ogSite),
    og_locale: pickText(record.og_locale) ?? pickText(record.ogLocale),
    seo_fallback_image:
      pickText(record.seo_fallback_image) ?? pickText(record.seoFallbackImage),
    tw_card: pickTwCard(record.tw_card) ?? pickTwCard(record.twCard),
    tw_title: pickText(record.tw_title) ?? pickText(record.twTitle),
    tw_desc: pickText(record.tw_desc) ?? pickText(record.twDesc),
    tw_image: pickText(record.tw_image) ?? pickText(record.twImage),
    ga4_id: pickText(record.ga4_id) ?? pickText(record.ga4Id),
  }) as Partial<LandingPageSEO>;
}

export function mapLandingPageData(
  landing: Partial<LandingContent> | null | undefined,
): LandingPageData {
  const landingRecord = asRecord(landing);
  const hero = asRecord(landingRecord?.hero);
  const contraste = asRecord(landingRecord?.contraste);
  const objecoes = asRecord(landingRecord?.objecoes);

  function resolveHeroTitleParts(record: UnknownRecord | undefined): { title_part1?: string; title_part2?: string } {
    const part1 = pickText(record?.title_part1) ?? pickText(record?.titlePart1);
    const part2 = pickText(record?.title_part2) ?? pickText(record?.titlePart2);
    if (part1 && part2) return { title_part1: part1, title_part2: part2 };
    const legacy = (pickText(record?.title) ?? "").trim();
    if (!legacy) return { title_part1: part1, title_part2: part2 };
    const m = legacy.match(/^APRENDA(?:[\s\n\r]+)([\s\S]+)$/i);
    if (m) {
      return {
        title_part1: part1 ?? "APRENDA",
        title_part2: part2 ?? m[1].replace(/^\n+/, "").replace(/\n{2,}/g, "\n"),
      };
    }
    const firstBreak = legacy.search(/[\s\n]/);
    if (firstBreak > 0) {
      return {
        title_part1: part1 ?? legacy.slice(0, firstBreak).toUpperCase(),
        title_part2: part2 ?? legacy.slice(firstBreak).replace(/^[\s\n]+/, ""),
      };
    }
    return { title_part1: part1 ?? legacy, title_part2: part2 ?? "" };
  }
  const heroTitleParts = resolveHeroTitleParts(hero);

  return {
    hero: withDefinedValues({
      cta_primary: pickText(hero?.cta_primary) ?? FALLBACK_HERO.cta_primary,
      cta_secondary: pickText(hero?.cta_secondary) ?? FALLBACK_HERO.cta_secondary,
      badge: pickText(hero?.badge),
      title: pickText(hero?.title),
      title_part1: heroTitleParts.title_part1,
      title_part2: heroTitleParts.title_part2,
      subtitle: pickText(hero?.subtitle),
      bottom_label: pickText(hero?.bottom_label) ?? pickText(hero?.bottomLabel),
      bg_mobile:
        pickText(hero?.bg_mobile) ??
        pickText(hero?.bgMobile) ??
        pickText(hero?.bg_img) ??
        pickText(hero?.bgImg),
      slides: (() => {
        const slides = mapCollection<LandingPageHeroSlide>(hero?.slides, mapHeroSlide);
        return slides.length > 0 ? slides : undefined;
      })(),
    }) as LandingPageHero,
    heroHighlights: mapCollection<LandingPageHeroHighlight>(hero?.highlights, mapHeroHighlight),
    trilhas: mapCollection<LandingPageTrilha>(landingRecord?.trilhas, mapTrilha),
    assinaturaPlanos: mapCollection<LandingPagePlano>(landingRecord?.planos, mapPlano),
    instrutores: mapCollection<LandingPageInstrutor>(landingRecord?.instrutores, mapInstrutor),
    blogPreview: mapBlogPreview(landingRecord?.blog_preview ?? landingRecord?.blogPreview),
    footerFeaturedPosts: mapCollection<LandingPageFooterFeaturedPost>(
      landingRecord?.footer_featured_posts ?? landingRecord?.footerFeaturedPosts,
      mapFooterFeaturedPost,
    ),
    contrasteRows: mapCollection<LandingPageContrasteRow>(contraste?.rows, mapContrasteRow),
    objecoes: mapCollection<LandingPageObjecao>(objecoes?.items, mapObjecao),
    stats: mapStats(landingRecord?.stats),
    diferenciais: mapDiferenciais(landingRecord?.diferenciais),
    cta: mapCTA(landingRecord?.cta),
    quiz: mapQuiz(landingRecord?.quiz),
    seo: mapSEO(landingRecord?.seo),
  };
}

export function getLandingPageData(): LandingPageData {
  return mapLandingPageData(getLandingContent());
}
